import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import useIngestRequest, { REQUEST_STATE, channelIdFrom } from './useIngestRequest'
import usePopover from './usePopover'
import useAdminAccess from '../../services/hooks/useAdminAccess'
import { ButtonOutline } from '../GeneralComponents/ButtonOutline/ButtonOutline'
import successToast from '../Toasts/SuccessToast'
import errorToast from '../Toasts/ErrorToast'
import classes from './index.module.css'

/**
 * Start an artist synchronization from the artists list. Internal staff only.
 *
 * Gated on `isInternal`, NOT `hasFullAdminAccess` - that is also true for
 * ORGANIZATION_OWNER and ORGANIZATION_ADMINISTRATOR. The endpoint makes its
 * own check; this only decides whether to draw the door.
 */
/** Why we will not send it, in the terms of what was actually pasted. */
function refusal(reason) {
  if (reason === 'not_an_artist') return 'That points at a song or an album. Copy the artist’s channel link.'
  if (reason === 'unknown_host') return 'We only read YouTube links here.'
  if (reason === 'legacy_channel_url') return 'That is an old-style channel address. Copy the /channel/UC… link.'
  if (reason === 'looks_like_a_name') return 'Paste a YouTube channel link or ID, not a name.'
  if (reason === 'partial_channel_id') {
    return 'That channel ID is incomplete — it should be UC followed by 22 characters.'
  }
  return 'Ingestion needs the YouTube channel. Open the artist on YouTube Music and copy the /channel/UC… link.'
}

export default function AddArtistButton({ className }) {
  const { isInternal } = useAdminAccess()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const { state, submit, reset } = useIngestRequest()
  const anchorRef = useRef(null)

  const close = useCallback(() => {
    setOpen(false)
    setValue('')
    reset()
  }, [reset])

  usePopover(anchorRef, open, close)

  if (!isInternal) return null

  const found = value.trim() ? channelIdFrom(value) : null
  const ready = Boolean(found && found.ok)

  async function send() {
    const result = await submit(value.trim())
    if (result.ok) {
      close()
      successToast(
        result.reused
          ? 'That artist is already being synced. Follow it on the Ingest page — nothing was started twice.'
          : 'Sync started. Follow it on the Ingest page.'
      )
    } else if (result.reason === 'api_error') {
      // The server says something useful - "Use the artist's UC… channel ID",
      // "Ingestion is restricted to internal administrators" - so show it.
      errorToast(result.message || 'We could not start that. Please try again.')
    }
  }

  return (
    <span className={classes.anchor} ref={anchorRef}>
      <ButtonOutline
        color="pink"
        size="small"
        text="ADD ARTIST"
        className={className}
        onClick={() => (open ? close() : setOpen(true))}
      />

      {open && (
        <div className={`${classes.panel} ${classes.popover}`} role="dialog" aria-label="Synchronize an artist">
          <span className={classes.headline}>Synchronize an artist</span>
          <input
            className={classes.input}
            type="text"
            autoComplete="off"
            placeholder="YouTube channel link, or a UC… id"
            aria-label="YouTube channel link or id"
            value={value}
            onChange={e => setValue(e.target.value)}
          />

          {found && found.ok && (
            <span className={classes.read}>
              Channel ID: <code className={classes.code}>{found.channelId}</code>
            </span>
          )}
          {found && !found.ok && <span className={classes.bad}>{refusal(found.reason)}</span>}

          <div className={classes.actions}>
            <button type="button" className={classes.secondary} onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className={classes.primary}
              aria-disabled={!ready || state === REQUEST_STATE.SENDING}
              onClick={() => ready && state !== REQUEST_STATE.SENDING && send()}
            >
              {state === REQUEST_STATE.SENDING ? 'Starting…' : 'Start synchronization'}
            </button>
          </div>
        </div>
      )}
    </span>
  )
}

AddArtistButton.propTypes = { className: PropTypes.string }
